import { ISMSCommunication } from "shared";
import { TrashIcon } from "@heroicons/react/20/solid";
import { Tooltip } from "@mui/material";

interface SmsCommunicationProps {
  sms: ISMSCommunication;
  onDeleteFailedOrSuspended?: (actionId: string) => Promise<void>;
}

export function SmsCommunication({
  sms,
  onDeleteFailedOrSuspended,
}: SmsCommunicationProps) {
  const createdAt = sms.createdAt ? new Date(sms.createdAt) : null;
  const isOutbound = sms.direction === `outbound`;
  const isManual = !!sms.isManual;
  const isAutomatedResponse = !!sms.isAutomatedResponse;
  const normalizedStatus = sms.status?.toLowerCase();
  const isFailed = normalizedStatus === `failed`;
  const isSuspended = normalizedStatus === `suspended`;
  const canDeleteFailedOrSuspended = !!sms.actionId
    && isManual
    && (isFailed || isSuspended)
    && !!onDeleteFailedOrSuspended;
  const normalizedFrom = sms.from?.trim();
  const hasFrom = !!normalizedFrom && normalizedFrom.toUpperCase() !== `N/A`;
  const label = isOutbound
    ? isFailed
      ? isAutomatedResponse ? `Automated SMS Failed` : isManual ? `On Demand SMS Failed` : `SMS Failed`
      : isSuspended
        ? isAutomatedResponse ? `Automated SMS Suspended` : isManual ? `On Demand SMS Suspended` : `SMS Suspended`
        : isAutomatedResponse ? `Automated SMS Sent` : isManual ? `On Demand SMS Sent` : `SMS Sent`
    : `SMS Reply Received`;
  const wrapperClass = isOutbound ? `flex justify-end` : `flex justify-start`;
  const headerClass = isOutbound ? `flex items-center justify-end gap-2 mb-1` : `flex items-center justify-start gap-2 mb-1`;
  const outboundPillClass = isFailed
    ? `px-2 py-0.5 text-[10px] font-medium rounded-full border text-red-700 bg-red-100 border-red-200`
    : isSuspended
      ? `px-2 py-0.5 text-[10px] font-medium rounded-full border text-amber-700 bg-amber-100 border-amber-200`
      : `px-2 py-0.5 text-[10px] font-medium rounded-full border text-indigo-700 bg-indigo-100 border-indigo-200`;
  const pillClass = isOutbound
    ? outboundPillClass
    : `px-2 py-0.5 text-[10px] font-medium rounded-full border text-teal-700 bg-teal-100 border-teal-200`;
  const outboundBubbleClass = isFailed
    ? `rounded-2xl rounded-tr-md bg-red-50 border border-red-200 shadow-xs overflow-hidden`
    : isSuspended
      ? `rounded-2xl rounded-tr-md bg-amber-50 border border-amber-200 shadow-xs overflow-hidden`
      : `rounded-2xl rounded-tr-md bg-indigo-50 border border-indigo-200 shadow-xs overflow-hidden`;
  const bubbleClass = isOutbound
    ? outboundBubbleClass
    : `rounded-2xl rounded-tl-md bg-teal-50 border border-teal-200 shadow-xs overflow-hidden`;

  return (
    <div className={ wrapperClass }>
      <div className={ `max-w-xl` }>
        <div className={ headerClass }>
          <div className={ `flex items-center gap-2` }>
            <span className={ pillClass }>
              { label }
            </span>
            { canDeleteFailedOrSuspended ? (
              <button
                type={ `button` }
                className={ `text-gray-500 hover:text-red-700` }
                onClick={ () => onDeleteFailedOrSuspended(sms.actionId as string) }
              >
                <Tooltip title={ `Remove failed/suspended SMS` }>
                  <TrashIcon className={ `h-4 w-4` } />
                </Tooltip>
              </button>
            ) : null }
          </div>
          <div className={ `text-xs text-gray-500` }>
            { createdAt ? createdAt.toLocaleString() : `` }
          </div>
        </div>
        <div className={ bubbleClass }>
          <div className={ `px-4 py-3` }>
            <div className={ `text-xs text-gray-500` }>
              { hasFrom ? (
                <>
                  { `From: ` }<span className={ `font-medium text-gray-700` }>{ normalizedFrom }</span>
                </>
              ) : null }
              { sms.to ? (
                <>
                  { hasFrom ? ` · ` : `` }
                  { `To: ` }<span className={ `font-medium text-gray-700` }>{ sms.to }</span>
                </>
              ) : null }
            </div>
            <div className={ `mt-2 text-sm text-gray-900 whitespace-pre-wrap wrap-break-word` }>
              { sms.body || `-` }
            </div>
            { (isFailed || isSuspended) && sms.errorMessage ? (
              <div className={ `mt-2 text-xs font-medium text-red-700 whitespace-pre-wrap wrap-break-word` }>
                { sms.errorMessage }
              </div>
            ) : null }
          </div>
        </div>
        { sms.sentByUserEmail ? (
          <div className={ `mt-1 px-1 text-xs text-gray-500` }>
            { `Sent by ` }<span className={ `font-medium text-gray-600` }>{ sms.sentByUserEmail }</span>
          </div>
        ) : null }
      </div>
    </div>
  );
}
